import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'
import styles from './BottomSheet.module.css'
import {
  lastSheetTrigger,
  lockPageScroll,
  scrolledFrom,
  sheetKeyDown,
  sheetOpener,
  swipeCloses,
  trapTab,
} from 'shared-components/layout/sheet-helpers'

/**
 * The community's one bottom sheet (Community mobile §5): a dimmed backdrop, a panel with 16px
 * top corners and a drag handle, max 90vh with its own scroll — or, with `contentScroll`, a
 * flex column whose content picks the one child that scrolls (the guide's pick sheet: fixed
 * head and segments, a scrolling body, a foot that stays in view). It is modal — Tab cycles
 * inside (`trapTab`), Escape closes (unless the caller's own listener owns Escape, as the badge
 * popover's disclosure group does), a backdrop tap or a swipe down on the handle (or on the panel
 * while nothing under the finger is scrolled down) closes, and the page under it does not scroll
 * (`lockPageScroll`: <html> AND <body>, cc9ce959).
 *
 * Rendered only while open, in a portal — so never in the shared-cached HTML. Users: the badge
 * popover's small-screen sheet (`BadgePanel`) and the Early Owner hero's mobile row; the mobile
 * M2/M3 sheets (badge cards, Category, Link gear) reuse it; so does a guide pick's phone sheet
 * (`owner-notes/guide/PickSheet`, `contentScroll`).
 */

// The steps live in the shared package (one copy for SG's sheets and the article conversation's
// `ConversationSheet`, both brands); re-exported so every importer of this file is unchanged.
export { trapTab, sheetKeyDown, lockPageScroll, sheetOpener, scrolledFrom, swipeCloses }

export interface BottomSheetProps {
  /** The dialog's id (an `aria-controls` target). */
  id?: string
  /** The id of the element that names the dialog. */
  labelledBy: string
  onClose: () => void
  /** false when the caller already closes on Escape (the badge disclosure group's listener). */
  closeOnEscape?: boolean
  /** false when the caller moves focus itself (BadgePanel focuses its ×); else the panel takes
   *  focus after the frame so a keyboard reader lands inside. */
  focusOnOpen?: boolean
  /** false when the caller hands focus back itself (the badge disclosure group); else the
   *  element focused before the sheet opened gets focus back on close. */
  restoreFocus?: boolean
  /** Draw the sheet's own × (a sheet whose content has no close control of its own). */
  withClose?: boolean
  /** The panel is a flex column that does not scroll itself: the caller's content gives one
   *  child `flex: 1 1 auto; min-height: 0; overflow-y: auto` and the rest stay in view. */
  contentScroll?: boolean
  /** Extra attributes on the layer (the badge popover marks it a popover zone). */
  layerAttrs?: Record<string, string>
  className?: string
  children: React.ReactNode
}

const BottomSheet: React.FC<BottomSheetProps> = ({
  id,
  labelledBy,
  onClose,
  closeOnEscape = true,
  focusOnOpen = true,
  withClose = false,
  contentScroll = false,
  restoreFocus = true,
  layerAttrs,
  className,
  children,
}) => {
  const box = useRef<HTMLDivElement>(null)
  // The latest onClose, so the listeners below never re-bind on a parent render.
  const close = useRef(onClose)
  close.current = onClose
  // The control that opened the sheet, read on the first render — before a child's autoFocus
  // (the picker's field) moves focus inside. The sheet mounts only after a press, in a browser.
  const opener = useRef<Element | null>(null)
  if (opener.current === null && typeof document !== 'undefined') {
    opener.current = sheetOpener(document.activeElement, lastSheetTrigger(), document.body)
  }

  useEffect(() => {
    const sheet = box.current
    if (!sheet) {
      return undefined
    }
    const back = opener.current
    const restore = lockPageScroll(document)
    const onKey = (e: KeyboardEvent): void =>
      sheetKeyDown(e, sheet, () => close.current(), closeOnEscape)
    sheet.addEventListener('keydown', onKey)
    // After the frame (c9338ddd): the sheet's × when it has one, else the panel itself.
    const frame = focusOnOpen
      ? window.requestAnimationFrame(() => {
          const target = sheet.querySelector<HTMLElement>(`.${styles.close}`) ?? sheet
          target.focus({ preventScroll: true })
        })
      : 0
    return () => {
      restore()
      if (restoreFocus && back instanceof HTMLElement && back.isConnected) {
        back.focus({ preventScroll: true })
      }
      sheet.removeEventListener('keydown', onKey)
      if (frame) {
        window.cancelAnimationFrame(frame)
      }
    }
  }, [closeOnEscape, focusOnOpen, restoreFocus])

  // Swipe down: from the handle, or anywhere on the panel while nothing under the finger is
  // scrolled down (the panel, or the scrolling child of a `contentScroll` sheet).
  const drag = useRef<{ y: number; t: number; dy: number } | null>(null)
  const onTouchStart = (e: React.TouchEvent): void => {
    const sheet = box.current
    const fromHandle = (e.target as HTMLElement).closest?.(`.${styles.handle}`)
    if (
      !sheet ||
      e.touches.length !== 1 ||
      (!fromHandle && scrolledFrom(e.target as Element, sheet))
    ) {
      drag.current = null
      return
    }
    drag.current = { y: e.touches[0].clientY, t: Date.now(), dy: 0 }
  }
  const onTouchMove = (e: React.TouchEvent): void => {
    const sheet = box.current
    if (!drag.current || !sheet) {
      return
    }
    const dy = Math.max(0, e.touches[0].clientY - drag.current.y)
    drag.current.dy = dy
    sheet.style.transform = dy > 0 ? `translateY(${dy}px)` : ''
  }
  const onTouchEnd = (): void => {
    const sheet = box.current
    const done = drag.current
    drag.current = null
    if (!sheet || !done) {
      return
    }
    if (swipeCloses(done.dy, Date.now() - done.t)) {
      close.current()
    } else {
      sheet.style.transform = ''
    }
  }

  // The backdrop closes on its own click — closing on pointerdown would unmount it and drop the
  // click on whatever lies underneath.
  return createPortal(
    <div className={styles.layer} {...layerAttrs}>
      <div className={styles.backdrop} onClick={() => close.current()} aria-hidden="true" />
      <div
        id={id}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        className={clsx(styles.sheet, contentScroll && styles.contentScroll, className)}
        ref={box}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        <span className={styles.handle} aria-hidden="true" />
        {withClose && (
          <button
            type="button"
            className={styles.close}
            aria-label="Close"
            onClick={() => close.current()}
          >
            {'×'}
          </button>
        )}
        {children}
      </div>
    </div>,
    document.body
  )
}

export default BottomSheet
